import { Markdown } from "./Markdown";

/**
 * Фрагмент Excel-таблицы
 */
export default class TableFragment {
  /**
   * @param {[number, number]} dim - два числа, размеры фрагмента (число клеток по ширине и высоте)
   * @param {object} frag
   * @param frag.data - матрица с данными
   * @param {Markdown[]} frag.markdown - массив стилей разметки
   * @param [frag.columnWidth] - массив ширин
   * @param [frag.rowHeight] - массив высот
   */
  constructor([n, m], { data, markdown, columnWidth, rowHeight }) {
    if (!columnWidth) columnWidth = new Array(n).fill({});
    if (!rowHeight) rowHeight = new Array(m).fill({});
    this.n = n;
    this.m = m;
    this.data = data;
    this.markdown = markdown;
    this.columnWidth = columnWidth;
    this.rowHeight = rowHeight;
    TableFragment.validate(this);
  }

  static validate({ n, m, data, markdown, columnWidth, rowHeight }) {
    if (data.length !== m) throw new Error(`data.length=${data.length} is expected to be ${m}`);
    data.forEach((row, i) => {
      if (row.length !== n) throw new Error(`data[i].length=${data[i].length} is expected to be ${n}`);
    });
    if (!Array.isArray(markdown)) throw new Error(`markdown is not an array`);
    markdown.forEach((md, i) => {
      if (md instanceof Markdown) return;
      markdown[i] = Markdown.fromJSON(md);
      // TODO: check markdown to affect the range [0..n]*[0..m]
    });
    if (columnWidth.length !== n) throw new Error(`columnWidth.length=${columnWidth.length} is expected to be ${n}`);
    if (rowHeight.length !== m) throw new Error(`rowHeight.length=${columnWidth.length} is expected to be ${m}`);
  }

  /**
   * Пустой фрагмент указанного размера.
   * @param {[number, number]} dir - размеры фрагмента
   * @return {TableFragment}
   */
  static empty([n, m]) {
    return new TableFragment([n, m], {
      data: new Array(m).fill(0).map(() => new Array(n).fill(0).map(() => undefined)),
      markdown: [],
      columnWidth: undefined,
      rowHeight: undefined,
    });
  }

  /**
   * Создать фрагмент на основе прямоугольной таблицы с данными.
   * @param data
   * @return {TableFragment}
   */
  static plainData(data) {
    if (data.length === 0) return TableFragment.empty([0, 0]);
    const n = data[0].length;
    const m = data.length;
    return new TableFragment([n, m], {
      data,
      markdown: [],
      columnWidth: undefined,
      rowHeight: undefined,
    });
  }

  /**
   * Поместить меньший фрагмент как часть текущего.
   * @param {TableFragment} frag
   * @param {number[]} offset - положение ячейки 0,0 вставляемого фрагмента
   * @return {TableFragment} this
   */
  place(frag, [x0, y0]) {
    // TODO: Нормальные сообщения для диагностики ошибок
    if (frag.n + x0 > this.n) throw new Error('out of range');
    if (frag.m + y0 > this.m) throw new Error('out of range');
    for (let dx = 0; dx < frag.n; ++dx) {
      for (let dy = 0; dy < frag.m; ++dy) {
        this.data[y0 + dy][x0 + dx] = frag.data[dy][dx];
      }
    }
    this.markdown.push(...frag.markdown.map((md) => md.shift(x0, y0)));
    for (let dx = 0; dx < frag.n; ++dx) {
      if (frag.columnWidth[dx] === undefined)
        continue;
      if (JSON.stringify(this.columnWidth[x0 + dx]) !== JSON.stringify(frag.columnWidth[dx]))
        throw new Error('columnWidth mismatches');
      this.columnWidth[x0 + dx] = frag.columnWidth[dx];
    }
    for (let dy = 0; dy < frag.m; ++dy) {
      if (frag.rowHeight[dy] === undefined)
        continue;
      if (JSON.stringify(this.rowHeight[y0 + dy]) !== JSON.stringify(frag.rowHeight[dy]))
        throw new Error('rowHeight mismatches');
      this.rowHeight[y0 + dy] = frag.rowHeight[dy];
    }
  }
}
